/**
 * Per-drone waypoint progress — derives which waypoints on the planned
 * route (`OiLiveEnvelope.waypoints`) are done / next / upcoming, purely
 * from confirmed backend data:
 *
 *   - `OiLiveEnvelope.waypoints`: the ordered `[lng, lat]` route, already
 *     live via `networkid.ext.waypoints` (see schemas/oi.ts).
 *   - `AlertEnvelope` fields from a `source_event: "waypoint_pred"` alert
 *     (ENG-86, confirmed against a real SIMTL70 simulator capture):
 *     `waypoint_index`, `target_lat`/`target_lng`, `distance_m`,
 *     `eta_seconds`.
 *
 * What this deliberately does NOT do
 * -----------------------------------
 * BE has not confirmed whether `waypoint_index` is 0- or 1-based, or
 * whether it lines up 1:1 with the `waypoints` array's order — so this
 * module never trusts it as a raw array offset. It cross-checks the
 * reported index against the alert's own `target_lat`/`target_lng`
 * (also BE-confirmed fields) and falls back to nearest-coordinate
 * matching when they disagree. If nothing matches within tolerance, the
 * route is reported `unknown` rather than guessed.
 *
 * `source_event: "waypoint_approached"` alerts today (as of the ENG-86
 * sandbox capture) carry no structured index — this module does not parse
 * one out of the free-text `message` field. We asked BE (ENG-40 ticket
 * comment, 2026-09-24) to add a `waypointIndex` field to
 * `waypoint_approached`, matching what `waypoint_pred` already sends. Two
 * asks were made; only the first is implemented here — see below.
 *
 * Forward-compatible with that ask, without any further FE change needed:
 * `useWaypointProgressStore.applyAlert` (the caller) trusts
 * `AlertEnvelope.waypoint_index` directly the moment a `waypoint_approached`
 * alert carries one — bypassing the fallback below entirely — and records
 * that this drone's BE session sends structured indices. Only while that
 * hasn't happened yet does the store fall back to `stampApproached()`
 * attributing the event to whatever index the drone's last `waypoint_pred`
 * was tracking (see that function's own doc comment).
 *
 * `missedDetectionEnabled` (param below) is what that "structured indices
 * confirmed" signal unlocks: once true, an index below the current target
 * with no recorded `passedAt` is reported `missed` instead of silently
 * folded into `done` — real missed/skipped-waypoint detection (ENG-40's
 * DoD item), safe to turn on specifically because indices are no longer
 * guessed. While false (today's BE behaviour, no structured index yet),
 * every index below the target still reads `done` — the honest "we can't
 * tell a skip from an unreported approach" fallback, not a guess.
 *
 * The second ask — BE listing every waypoint reached so far (index + time),
 * for late joiners — shipped as `reachedWaypoints` on `waypoint_approached`
 * (ENG-211, confirmed live on sandbox 2026-10-06; parsed to
 * `AlertEnvelope.reached_waypoints`). `seedPassedAtFromReached()` below folds
 * the entries for the alert's current `lap` into `passedAt`, so a dashboard
 * opened mid-flight gets every earlier "reached at" time from the next alert.
 * BE caps the list at 500 and it spans many laps on a looping route, hence the
 * lap filter.
 *
 * "Missed" now comes from BE itself (ENG-211, 2026-10-06): every entry carries
 * its closest approach (`distance_m`) and a `missed` verdict — true when the
 * drone passed the waypoint but never got within BE's threshold (20 m,
 * configurable on their side). `buildWaypointProgress` uses that flag
 * directly. Waypoints before the first entry of the FIRST tracked lap were
 * never examined (tracking started mid-route), so they read `untracked`, not
 * missed — see `untrackedBelowIndex`. The old inference ("below the target
 * with no recorded arrival = missed") survives only as a fallback for a BE
 * that sends no flags.
 *
 * `isLapWrap()` detects the mission restarting (see its own doc comment)
 * so the caller can clear stale `passedAt` state instead of leaving
 * everything stuck `done` forever after the first lap.
 */

import { distance } from '@turf/turf';

export interface WaypointPredAlert {
  waypointIndex: number;
  targetLat: number;
  targetLng: number;
  distanceMeters: number | null;
  etaSeconds: number | null;
  /** Alert timestamp, ms epoch — the moment `etaSeconds` was predicted for. */
  ts: number;
}

export type WaypointStatus = 'done' | 'next' | 'upcoming' | 'unknown' | 'missed' | 'untracked';

/** What BE reported when a waypoint counted as reached. `missed` is null when
 *  BE sent no verdict (older job), in which case the flag-less fallback runs. */
export interface ReachedInfo {
  missed: boolean | null;
  distanceM: number | null;
}

/** Tracking facts for one drone's current lap, derived from
 *  `reachedWaypoints` by the store. */
export interface WaypointTracking {
  /** index -> BE's report, for the current lap only. */
  reached?: Readonly<Record<number, ReachedInfo>>;
  /** Waypoints below this index were never examined (tracking started
   *  mid-route on the first lap). Null/absent when everything is tracked or it
   *  can't be told. */
  untrackedBelow?: number | null;
  /** BE has sent per-waypoint verdicts for this drone this session. Sticky: it
   *  must survive a lap wrap, which clears `reached` — otherwise the first
   *  waypoint passed on the new lap would fall back to the guesswork path. */
  verdicts?: boolean;
}

/** How many waypoints directly behind the target can still be waiting for BE's
 *  report. `waypoint_pred` moves to the next target 0.5–8 s BEFORE the
 *  `waypoint_approached` alert for the one just passed arrives (observed live,
 *  SIMTL70, 2026-10-07), so that gap is normal, not "never tracked". */
const PENDING_WINDOW = 2;

export interface WaypointProgressPoint {
  index: number;
  lng: number;
  lat: number;
  status: WaypointStatus;
  /** Only set on the `next` point — BE gives us a prediction for the
   *  immediate target only, not the whole remaining route. */
  etaSeconds: number | null;
  distanceMeters: number | null;
  /** Client-observed pass time (ISO), set the first tick this point was
   *  seen to have fallen behind the current target. Null until observed
   *  — we don't know when a waypoint was passed before we started
   *  watching, and won't fabricate a time for it. */
  passedAt: string | null;
  /** BE has reported this waypoint as reached on the current lap — as opposed
   *  to a status inferred from the target index. */
  reached: boolean;
  /** BE's closest-approach distance for a reached waypoint, metres. */
  closestApproachM: number | null;
  /** Passed (the target has moved on) but BE's report with the time and verdict
   *  hasn't arrived yet. Status is `done` meanwhile; the time follows. */
  pending: boolean;
}

/** Coordinate match tolerance, in degrees (~20 m at the equator). Was
 *  originally ~110 m ("wide enough to absorb float rounding") — too wide
 *  in practice: a live capture against the real ENG-86 route showed
 *  consecutive waypoints as close as ~80 m apart, so whenever BE's
 *  reported index didn't exactly confirm, the old nearest-coordinate
 *  fallback below could snap onto the WRONG adjacent waypoint instead of
 *  the intended one — confirmed as the cause of "next" landing on a
 *  waypoint the drone had already passed. Tight enough now to only ever
 *  catch genuine floating-point rounding (real captures show 0 m exact
 *  matches), never neighbor confusion. */
const MATCH_TOLERANCE_DEG = 1.8e-4;

function dist(a: readonly [number, number], lng: number, lat: number): number {
  return Math.hypot(a[0] - lng, a[1] - lat);
}

/**
 * Resolve which array index in `waypoints` the alert's target corresponds
 * to. Prefers the reported `waypointIndex` when the coordinate at that
 * array position actually matches the alert's `target_lat`/`target_lng`;
 * otherwise searches for a near-exact coordinate match elsewhere. If
 * neither confirms anything within the (tight) tolerance, trusts BE's own
 * reported `waypointIndex` directly as long as it's a real position in the
 * route — deliberately does NOT fall back to "closest guess among several
 * candidates" anymore, since that guess is exactly what produced the wrong
 * answer in practice; an unconfirmed-but-in-bounds index from BE is more
 * trustworthy than a coordinate-proximity guess on a route with waypoints
 * this close together. Returns null only when the index itself is out of
 * bounds — an honest "can't tell", not a guess.
 */
export function resolveNextWaypointArrayIndex(
  waypoints: ReadonlyArray<readonly [number, number]>,
  pred: WaypointPredAlert,
  toleranceDeg = MATCH_TOLERANCE_DEG,
): number | null {
  const reported = waypoints[pred.waypointIndex];
  if (reported && dist(reported, pred.targetLng, pred.targetLat) <= toleranceDeg) {
    return pred.waypointIndex;
  }
  let bestIdx: number | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  waypoints.forEach((wp, i) => {
    const d = dist(wp, pred.targetLng, pred.targetLat);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  });
  if (bestDist <= toleranceDeg) return bestIdx;
  return reported ? pred.waypointIndex : null;
}

/** Build the per-waypoint status list for the route. `pred` is the latest
 *  `waypoint_pred` alert for this drone, or null when none has arrived yet
 *  (e.g. mock/demo mode, which intentionally emits no alerts).
 *
 *  `passedAt` (real `waypoint_approached` timestamps — see `stampApproached`)
 *  is also the STATUS source of truth for "done", not just tooltip text:
 *  once an index is stamped, it stays `done` even if a later `waypoint_pred`
 *  reports a lower `waypointIndex`. That happens for real on this ticket's
 *  own test mission — Maykon's ENG-86 comment: after the last waypoint the
 *  simulated flight loops back to the first and continues, instead of
 *  landing. Without this, a lap reset would visually un-mark every waypoint
 *  already flown, which read as "waypoints don't stay darker" / "lose their
 *  tooltip" once the mission looped.
 *
 *  `missedDetectionEnabled` (ENG-40 ask #1 — see module header) — only the
 *  caller (`useWaypointProgressStore`) knows whether this drone's BE
 *  session has ever sent a structured `waypoint_index` on
 *  `waypoint_approached`; while it hasn't, an index below `next` with no
 *  `passedAt` stays `done` rather than guessed `missed`. */
export function buildWaypointProgress(
  waypoints: ReadonlyArray<readonly [number, number]>,
  pred: WaypointPredAlert | null,
  passedAt: Readonly<Record<number, string>> = {},
  missedDetectionEnabled = false,
  tracking: WaypointTracking = {},
): WaypointProgressPoint[] {
  const nextIdx = pred ? resolveNextWaypointArrayIndex(waypoints, pred) : null;
  const reachedInfo = tracking.reached ?? {};
  const untrackedBelow = tracking.untrackedBelow ?? null;
  // BE's verdicts are in use for this drone: from here a waypoint below the
  // target that BE never reported is "not tracked", not a guessed miss.
  const flagged =
    tracking.verdicts === true || Object.values(reachedInfo).some((r) => r.missed != null);
  // Waypoints from here up to the target are "passed, report on its way".
  let lastReported = -1;
  for (let k = 0; k < waypoints.length; k++) {
    if (nextIdx != null && k >= nextIdx) break;
    if (reachedInfo[k] != null || passedAt[k] != null) lastReported = k;
  }
  const pendingFrom = Math.max(
    lastReported + 1,
    untrackedBelow ?? 0,
    nextIdx != null ? nextIdx - PENDING_WINDOW : 0,
  );
  return waypoints.map(([lng, lat], i) => {
    const everPassed = passedAt[i] != null;
    const info = reachedInfo[i];
    let status: WaypointStatus;
    let pending = false;
    if (nextIdx != null && i === nextIdx) {
      status = 'next';
    } else if (info?.missed === true) {
      status = 'missed';
    } else if (info?.missed === false || everPassed) {
      status = 'done';
    } else if (untrackedBelow != null && i < untrackedBelow) {
      status = 'untracked';
    } else if (nextIdx != null && i < nextIdx) {
      if (flagged) {
        pending = i >= pendingFrom;
        status = pending ? 'done' : 'untracked';
      } else {
        status = missedDetectionEnabled ? 'missed' : 'done';
        // Inferred `done` (the target moved past it, nothing reported yet) is
        // the same "passed, report pending" state — not a time we lost.
        pending = !missedDetectionEnabled;
      }
    } else if (nextIdx == null) {
      status = 'unknown';
    } else {
      status = 'upcoming';
    }
    return {
      index: i,
      lng,
      lat,
      status,
      etaSeconds: status === 'next' ? (pred!.etaSeconds ?? null) : null,
      distanceMeters: status === 'next' ? (pred!.distanceMeters ?? null) : null,
      passedAt: passedAt[i] ?? null,
      reached: info != null || everPassed,
      closestApproachM: info?.distanceM ?? null,
      pending,
    };
  });
}

/** A `waypoint_pred` older than this no longer drives the final-waypoint
 *  estimate. BE (and the demo source) send one per second; the 2026-09-28
 *  sandbox capture also showed ~70 s pipeline stalls, during which a frozen
 *  estimate would read as live. */
export const FINAL_ETA_STALE_MS = 15_000;

/** Below this implied speed the drone is effectively hovering, and
 *  remaining-distance / speed stops meaning anything. */
const MIN_ESTIMATE_SPEED_MPS = 0.5;

export interface FinalWaypointEstimate {
  /** Seconds to the final waypoint as of `nowMs`, clamped at 0. */
  etaSeconds: number;
  /** Current-leg distance plus every remaining leg, in metres. */
  distanceMeters: number;
}

/**
 * Client-side estimate of the time/distance to the route's LAST waypoint.
 * BE only predicts the immediate target (`waypoint_pred`), so this extends
 * that prediction over the rest of the route using BE's own method: its
 * `etaSeconds` is straight-line distance / current ground speed (verified
 * against the live sandbox stream, 2026-09-28), so the remaining legs are
 * summed as straight lines and divided by the same implied speed
 * (`distanceMeters / etaSeconds`). Ignores turns, holds and per-leg speed
 * changes — an estimate, and only as good as BE's current target.
 *
 * Returns null when there's nothing honest to show: no resolvable target,
 * no usable current-leg ETA/distance, a near-stationary drone, a stale
 * prediction, or when the final waypoint is itself the current target (BE's
 * own `etaSeconds` already covers that case exactly).
 */
export function estimateFinalWaypoint(
  waypoints: ReadonlyArray<readonly [number, number]>,
  pred: WaypointPredAlert,
  nowMs: number,
): FinalWaypointEstimate | null {
  const ageMs = Math.max(0, nowMs - pred.ts);
  if (ageMs > FINAL_ETA_STALE_MS) return null;
  const nextIdx = resolveNextWaypointArrayIndex(waypoints, pred);
  const lastIdx = waypoints.length - 1;
  if (nextIdx == null || nextIdx >= lastIdx) return null;
  const { etaSeconds, distanceMeters } = pred;
  if (etaSeconds == null || distanceMeters == null || !(etaSeconds > 0)) return null;
  const speedMps = distanceMeters / etaSeconds;
  if (!(speedMps >= MIN_ESTIMATE_SPEED_MPS)) return null;

  let remainingM = 0;
  for (let i = nextIdx; i < lastIdx; i++) {
    remainingM += distance(waypoints[i] as [number, number], waypoints[i + 1] as [number, number], {
      units: 'meters',
    });
  }
  return {
    etaSeconds: Math.max(0, etaSeconds + remainingM / speedMps - ageMs / 1000),
    distanceMeters: distanceMeters + remainingM,
  };
}

/** Margin (in array positions) an index must be within of the route's
 *  start/end to count as "near" it for `isLapWrap`. Real routes seen so
 *  far run ~20 waypoints; on a route shorter than ~2×margin the two ends
 *  overlap and this heuristic can't distinguish a wrap from an ordinary
 *  short step — accepted limitation, not seen in practice yet. */
export const LAP_WRAP_MARGIN = 2;

/** True when the target jumped from near the final waypoint back to near
 *  the first one — the drone completed the whole route and restarted it
 *  (ENG-86, Maykon: the sim loops instead of landing), as opposed to an
 *  ordinary forward step or an out-of-order alert delivery mid-route
 *  (which would leave both indices somewhere in the middle, not one at
 *  each end). Deliberately narrow for that reason — "any index decrease"
 *  alone isn't a safe signal, since a single out-of-order `waypoint_pred`
 *  could otherwise wipe out real progress on a false trigger. */
export function isLapWrap(
  waypointsLength: number,
  prevIdx: number | null,
  nextIdx: number | null,
): boolean {
  if (prevIdx == null || nextIdx == null) return false;
  if (nextIdx >= prevIdx) return false;
  const nearEnd = prevIdx >= waypointsLength - 1 - LAP_WRAP_MARGIN;
  const nearStart = nextIdx <= LAP_WRAP_MARGIN;
  return nearEnd && nearStart;
}

/** Record the real arrival time for a waypoint just confirmed by a
 *  `waypoint_approached` alert — the ticket's other confirmed ENG-86 message
 *  type, which carries no structured waypoint index (see schemas/alerts.ts)
 *  but does carry a real `ts`. `approachedIndex` is resolved by the caller
 *  from the drone's PREVIOUS `waypoint_pred` target (the waypoint a
 *  `waypoint_approached` alert refers to is, by construction, whatever the
 *  live prediction was tracking right before it fired) — not parsed from
 *  the alert's free-text message.
 *
 *  Always takes the alert's timestamp over whatever was stored before: it's
 *  the authoritative real arrival time, strictly better than a guess. Pure —
 *  returns the same reference when nothing actually changed. */
export function stampApproached(
  prevPassedAt: Readonly<Record<number, string>>,
  approachedIndex: number,
  approachedAtIso: string,
): Record<number, string> {
  if (prevPassedAt[approachedIndex] === approachedAtIso) return prevPassedAt;
  return { ...prevPassedAt, [approachedIndex]: approachedAtIso };
}

/** One entry of `AlertEnvelope.reached_waypoints`. */
export interface ReachedWaypointEntry {
  waypoint_index: number;
  lap: number;
  reached_at: string;
  distance_m?: number | null;
  missed?: boolean | null;
}

/** BE caps `reachedWaypoints` at this many entries (oldest dropped). */
const REACHED_LIST_CAP = 500;

/** BE's per-waypoint report for `lap`, keyed by waypoint index. Same lap and
 *  route-bounds filtering as `seedPassedAtFromReached`. Pure — returns the same
 *  reference when nothing changed. */
export function seedReachedInfoFromHistory(
  prev: Readonly<Record<number, ReachedInfo>>,
  reached: ReadonlyArray<ReachedWaypointEntry>,
  lap: number,
  routeLength: number | null,
): Record<number, ReachedInfo> {
  let next: Record<number, ReachedInfo> | null = null;
  for (const e of reached) {
    if (e.lap !== lap) continue;
    if (routeLength != null && e.waypoint_index >= routeLength) continue;
    const info: ReachedInfo = { missed: e.missed ?? null, distanceM: e.distance_m ?? null };
    const had = (next ?? prev)[e.waypoint_index];
    if (had && had.missed === info.missed && had.distanceM === info.distanceM) continue;
    next ??= { ...prev };
    next[e.waypoint_index] = info;
  }
  return next ?? (prev as Record<number, ReachedInfo>);
}

/** Waypoints below the returned index were never tracked (BE: "tracking started
 *  mid-route"), so they must read `untracked`, not `missed`.
 *
 *  Only the FIRST lap BE tracked can have that gap — on every later lap the
 *  route restarts at waypoint 0 and is tracked in full. The list is oldest
 *  first, so the gap exists exactly when the current lap is the list's first
 *  lap and its first entry isn't waypoint 0. A list truncated at BE's cap has
 *  lost its start, so the boundary can't be known then; neither can it for an
 *  empty list (just reset by a re-plan). Returns null in all those cases. */
export function untrackedBelowIndex(
  reached: ReadonlyArray<ReachedWaypointEntry>,
  lap: number,
): number | null {
  const first = reached[0];
  if (!first || reached.length >= REACHED_LIST_CAP) return null;
  if (first.lap !== lap) return null;
  return first.waypoint_index > 0 ? first.waypoint_index : null;
}

/** Fold a `waypoint_approached` alert's `reachedWaypoints` history into
 *  `passedAt`, keeping only entries for `lap` (BE's list spans every lap on a
 *  looping route) and, when the route is known, indices inside it. The BE
 *  `reached_at` is the authoritative arrival time, so it overwrites whatever
 *  was stamped before. Pure — returns the same reference when nothing changed. */
export function seedPassedAtFromReached(
  prevPassedAt: Readonly<Record<number, string>>,
  reached: ReadonlyArray<ReachedWaypointEntry>,
  lap: number,
  routeLength: number | null,
): Record<number, string> {
  let next: Record<number, string> | null = null;
  for (const e of reached) {
    if (e.lap !== lap) continue;
    if (routeLength != null && e.waypoint_index >= routeLength) continue;
    if ((next ?? prevPassedAt)[e.waypoint_index] === e.reached_at) continue;
    next ??= { ...prevPassedAt };
    next[e.waypoint_index] = e.reached_at;
  }
  return next ?? (prevPassedAt as Record<number, string>);
}

/** `T+mm:ss` countdown label (mockup: "T+0:34"). Negative/NaN → null. */
export function formatCountdown(etaSeconds: number | null): string | null {
  if (etaSeconds == null || !Number.isFinite(etaSeconds) || etaSeconds < 0) return null;
  const total = Math.round(etaSeconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `T+${m}:${String(s).padStart(2, '0')}`;
}

/** "15 minutes ago" style label for a `passedAt` ISO timestamp, relative to
 *  `nowMs`. Matches the ticket's "15:16 (5 minutes ago)" hover format —
 *  callers prefix the clock-time portion themselves from `passedAt`. */
export function formatAgo(passedAtIso: string, nowMs = Date.now()): string {
  const deltaSec = Math.max(0, Math.round((nowMs - new Date(passedAtIso).getTime()) / 1000));
  if (deltaSec < 60) return 'just now';
  const min = Math.round(deltaSec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  return `${hr} hour${hr === 1 ? '' : 's'} ago`;
}
